import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { friendlyErrorMessage } from './errors';

type CreateApolloClientOptions = {
  uri: string;
  /** Called once when the session cookie is gone or expired. */
  onUnauthenticated: () => void;
  onError: (message: string) => void;
  /** Development fixtures replace the network (src/dev). */
  terminatingLink?: ApolloLink;
};

export function createCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      // Embedded objects without an id are owned by their workspace and replaced as a whole.
      Workspace: {
        fields: {
          usage: { merge: true },
          connections: { merge: true },
          billing: { merge: true },
        },
      },
      Viewer: { keyFields: [] },
    },
  });
}

export function createApolloClient({
  uri,
  onUnauthenticated,
  onError,
  terminatingLink,
}: CreateApolloClientOptions): ApolloClient {
  // The session is an httpOnly cookie on the same origin: the client never sees or refreshes a token.
  const httpLink = new HttpLink({ uri, credentials: 'same-origin' });

  const errorLink = new ErrorLink(({ error, operation }) => {
    const isUnauthenticated =
      CombinedGraphQLErrors.is(error) &&
      error.errors.some((graphQLError) => graphQLError.extensions?.code === 'UNAUTHENTICATED');
    if (isUnauthenticated) {
      onUnauthenticated();
      return;
    }
    if (operation.getContext().skipGlobalErrorToast) return;
    onError(friendlyErrorMessage(error));
  });

  return new ApolloClient({
    link: ApolloLink.from([errorLink, terminatingLink ?? httpLink]),
    cache: createCache(),
    defaultOptions: { watchQuery: { fetchPolicy: 'cache-and-network' } },
  });
}
